import { FormControl } from "@angular/forms";

/**
 * Date range interface for filter forms
 */
export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

/**
 * Strongly typed form controls interface for scheduled orders filters
 * Using FormControl<T> pattern for type safety
 * Note: PrimeNG datepicker in range mode stores Date[] not DateRange objects
 */
export interface IScheduledOrdersFilterControls {
  siteId: FormControl<number>;
  search: FormControl<string | null>;
  status: FormControl<string[]>;
  processingErrorCodes: FormControl<number[]>;
  customerCreatedRange: FormControl<Date[] | null>;
  scheduledOrderCreatedRange: FormControl<Date[] | null>;
  nextOccurrenceRange: FormControl<Date[] | null>;
  lastProcessedRange: FormControl<Date[] | null>;
  pageSize: FormControl<number>;
  page: FormControl<number>;
  sortBy: FormControl<string | null>;
  sortDirection: FormControl<"asc" | "desc">;
}

/**
 * Data interface for API filter requests
 * This maps to the old QPilotFilter structure
 */
export interface ScheduledOrdersFilter {
  siteId: number;
  search?: string;
  status?: string[];
  processingErrorCodes?: number[];
  customerCreatedFromDate?: Date;
  customerCreatedToDate?: Date;
  scheduledOrderCreatedFromDate?: Date;
  scheduledOrderCreatedToDate?: Date;
  startDate?: Date;
  endDate?: Date;
  lastProcessedFromDate?: Date;
  lastProcessedToDate?: Date;
  pageSize: number;
  page: number;
  orderBy?: string;
  order?: string;
  // Additional parameters to match old QMC behavior
  offset?: number; // Timezone offset
  active?: boolean; // Include active orders filter
  valid?: boolean; // Include valid orders filter
}

/**
 * Status options for scheduled orders
 */
export enum ScheduledOrderStatus {
  Active = "Active",
  Paused = "Paused",
  Failed = "Failed",
  Queued = "Queued",
  Processing = "Processing",
  Locked = "Locked",
  Deleted = "Deleted",
}

/**
 * Status filter options with display labels
 */
export interface StatusFilterOption {
  value: ScheduledOrderStatus;
  label: string;
  count?: number;
}

/**
 * Processing error code options
 * Based on ProcessingErrorCodesHelper from old component
 */
export interface ProcessingErrorCodeOption {
  code: number;
  description: string;
}

/**
 * Default filter values
 */
export const DEFAULT_FILTER_VALUES = {
  pageSize: 20,
  page: 1,
  sortDirection: "desc" as const, // Changed to desc: show most recent next occurrence first
  sortBy: "NextOccurrenceUtc",
  status: [] as string[],
  processingErrorCodes: [] as number[],
} as const;

/**
 * Page size options for pagination
 */
export const PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 50, 100] as const;
